import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useFormDraft } from "@/hooks/useFormDraft";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CalendarIcon, Plus, Trash2, ArrowRight, ArrowLeft, Loader2, ChevronDown, ChevronRight, Pencil, AlertTriangle, Copy, ChevronsUpDown, GripVertical, FileText, Link2, Calculator, Eye, Printer, Maximize2, Minimize2, RotateCcw } from "lucide-react";
import { usePersistentColumnWidths } from "@/hooks/usePersistentColumnWidths";
import { exportPdf } from "@/lib/export/exportPdf";
import { EstimatePrintTemplate, EstimatePrintData, EstimatePrintGroup, EstimatePrintLineItem } from "@/components/shared/print-templates/EstimatePrintTemplate";
import { isAlternateGroup } from "@/lib/estimateProposal";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { CustomerSelectWithCreate } from "@/components/controller/CustomerSelectWithCreate";
import {
  useCreateEstimate,
  useUpdateEstimate,
  LineItem,
  SubLine,
  recalcLineItem,
  normalizeEstimateLineItems,
  JobType,
  Estimate,
  isNewFormat,
  QuantityMode,
  QuantityFormula,
  applyFormula,
  EstimateGroup,
  EstimatePrintSettings,
  DEFAULT_PRINT_SETTINGS,
} from "@/hooks/useEstimates";
import { useEstimateTemplates, EstimateTemplate, TemplateLineItem } from "@/hooks/useEstimateTemplates";
import { useEstimatorSettings } from "@/hooks/useEstimatorSettings";
import { useProductsServices } from "@/hooks/useProductsServices";
import { useProposalPresets, findPresetForJobType, EstimateProposalPreset } from "@/hooks/useEstimateProposalPresets";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const UNITS = ["SF", "LF", "EA", "LS", "HR", "DAY", "TON", "GAL", "BAG", "ROLL", "SQ", "BOX", "BDL"];

const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

// Product codes prioritized by job type
const JOB_TYPE_PRIORITY: Record<string, string[]> = {
  flooring: ['LAB-001','LAB-002','LAB-003','LAB-004','LAB-005','LAB-006','LAB-007','MAT-001','MAT-002','MAT-003','MAT-004','MAT-005','MAT-006','MAT-007','MAT-008','MAT-009','MAT-010','MAT-011','MAT-012','MAT-013','MAT-014','MAT-015'],
  roofing: ['LAB-009','LAB-014','MAT-016','MAT-017','MAT-018','MAT-019'],
  emergency: ['LAB-008','EQP-004','EQP-005','EQP-006'],
};

interface SubLineDraft {
  id: string;
  type: "labor" | "material";
  description: string;
  qty: string;
  unit: string;
  cost_price: string;
  markup_pct: string;
  unit_price: string;
  taxable: boolean;
  apply_vendor_tax: boolean;
  vendor_tax_rate: number;
  product_service_id?: string;
  income_account?: string;
  cogs_account?: string;
  sort_order: number;
  quantity_mode: QuantityMode;
  quantity_formula?: QuantityFormula | null;
}

interface LineDraft {
  id: string;
  label: string;
  description: string;
  customer_description: string;
  qty: string;
  unit: string;
  sub_lines: SubLineDraft[];
  expanded: boolean;
  sort_order: number;
  group_id?: string | null;
}

interface GroupDraft {
  id: string;
  name: string;
  group_type: "room" | "phase" | "trade" | "custom";
  sort_order: number;
  color: string;
  collapsed: boolean;
}

const GROUP_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"];

// ─── Pure propagation helpers ─────────────────────────────────────────

function getLineTriggerUnitDraft(line: LineDraft): string | null {
  const sorted = [...line.sub_lines].sort((a, b) => a.sort_order - b.sort_order);
  return sorted.find((sl) => sl.type === "labor")?.unit ?? null;
}

function normalizeDraftLine(line: LineDraft): LineDraft {
  const triggerUnit = getLineTriggerUnitDraft(line);
  return {
    ...line,
    sub_lines: line.sub_lines.map((sl) => {
      // 1. manual => preserve draft qty
      if (sl.quantity_mode === "manual") return sl;

      // 2. formula => compute from parent qty + parent unit
      if (sl.quantity_mode === "formula") {
        if (sl.quantity_formula) {
          const parentQty = parseFloat(line.qty) || 0;
          const computed = applyFormula(parentQty, line.unit, sl.quantity_formula);
          return { ...sl, qty: String(computed) };
        }
        // formula mode but missing/invalid formula => qty "0", keep mode formula
        return { ...sl, qty: "0" };
      }

      // 3. auto => trigger-unit logic
      if (!triggerUnit) return { ...sl, qty: "0" };
      return { ...sl, qty: sl.unit === triggerUnit ? line.qty : "0" };
    }),
  };
}

function normalizeDraftLines(lines: LineDraft[]): LineDraft[] {
  return lines.map((l, i) =>
    normalizeDraftLine({
      ...l,
      sort_order: i,
      sub_lines: l.sub_lines.map((sl, si) => ({ ...sl, sort_order: si })),
    })
  );
}

// ─── Utility helpers ──────────────────────────────────────────────────

const calcUnitPrice = (
  cost: string,
  markup: string,
  taxable: boolean = false,
  applyVendorTax: boolean = false,
  vendorTaxRate: number = 0
) => {
  const c = parseFloat(cost) || 0;
  const m = parseFloat(markup) || 0;
  const t = vendorTaxRate / 100;
  const effectiveCost = taxable && applyVendorTax ? c * (1 + t) : c;
  const preTaxPrice = effectiveCost * (1 + m / 100);
  return taxable && applyVendorTax ? preTaxPrice * (1 + t) : preTaxPrice;
};

const draftSubLine = (
  type: "labor" | "material",
  parentQty?: string,
  parentUnit?: string,
  parentDesc?: string,
  sortOrder?: number,
  quantityMode?: QuantityMode
): SubLineDraft => ({
  id: crypto.randomUUID(),
  type,
  description: parentDesc ? (type === "labor" ? `${parentDesc} - Labor` : parentDesc) : "",
  qty: parentQty ?? "1",
  unit: parentUnit ?? "SF",
  cost_price: "0",
  markup_pct: type === "labor" ? "35" : "20",
  unit_price: "0",
  taxable: type === "material",
  apply_vendor_tax: type === "material",
  vendor_tax_rate: 0,
  sort_order: sortOrder ?? 0,
  quantity_mode: quantityMode ?? "auto",
});

const emptyLine = (sortOrder?: number, groupId?: string | null): LineDraft => ({
  id: crypto.randomUUID(),
  label: "",
  description: "",
  customer_description: "",
  qty: "1",
  unit: "SF",
  sub_lines: [draftSubLine("labor", "1", "SF", "", 0, "auto")],
  expanded: true,
  sort_order: sortOrder ?? 0,
  group_id: groupId ?? null,
});

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

// Focus-toggled currency input: shows formatted $X.XX when blurred, raw number when editing.
const CurrencyCell: React.FC<{
  value: string;
  onChange: (v: string) => void;
  className?: string;
  title?: string;
  step?: string;
}> = ({ value, onChange, className, title, step = "0.01" }) => {
  const [focused, setFocused] = React.useState(false);
  const num = parseFloat(value) || 0;
  return (
    <Input
      type="text"
      inputMode="decimal"
      value={focused ? value : fmtCurrency(num)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9.\-]/g, ""))}
      className={cn("h-8 w-full text-right", className)}
      style={{ fontVariantNumeric: "tabular-nums" }}
      title={title}
      step={step}
    />
  );
};

const subLineBilled = (sl: SubLineDraft) => (parseFloat(sl.qty) || 0) * (parseFloat(sl.unit_price) || 0);
const subLineCost = (sl: SubLineDraft) => (parseFloat(sl.qty) || 0) * (parseFloat(sl.cost_price) || 0);

const calcLineTotal = (l: LineDraft) => l.sub_lines.reduce((s, sl) => s + subLineBilled(sl), 0);

function templateToLines(tpl: EstimateTemplate): LineDraft[] {
  return (tpl.line_items as TemplateLineItem[]).map((tli, idx) => {
    // Use new sub_lines structure if available
    const sub_lines: SubLineDraft[] = (tli.sub_lines && tli.sub_lines.length > 0)
      ? tli.sub_lines.map((tsl, si) => ({
          id: crypto.randomUUID(),
          type: tsl.type,
          description: tsl.description || (tsl.type === "labor" ? `${tli.description} - Labor` : tli.description),
          qty: "1",
          unit: tsl.unit || tli.unit,
          cost_price: "0",
          markup_pct: String(tsl.default_markup_pct ?? (tsl.type === "labor" ? 35 : 20)),
          unit_price: "0",
          taxable: tsl.type === "material",
          apply_vendor_tax: tsl.type === "material",
          vendor_tax_rate: 0,
          sort_order: tsl.sort_order ?? si,
          quantity_mode: tsl.quantity_mode ?? "auto",
          // Deep-clone formula as snapshot — template edits must NOT mutate existing estimates
          quantity_formula: tsl.quantity_formula ? JSON.parse(JSON.stringify(tsl.quantity_formula)) : null,
        }))
      : (tli.sub_line_types ?? []).map((t, si) =>
          draftSubLine(t, "1", tli.unit, tli.description, si, "auto")
        );
    sub_lines.forEach(sl => { sl.cost_price = "0"; sl.unit_price = "0"; });
    return normalizeDraftLine({
      id: crypto.randomUUID(),
      label: "",
      description: tli.description,
      customer_description: "",
      qty: "1",
      unit: tli.unit,
      sub_lines,
      expanded: true,
      sort_order: idx,
    });
  });
}

/** Convert draft lines to the final LineItem format */
function draftsToLineItems(lines: LineDraft[]): LineItem[] {
  const raw = lines
    .filter((l) => l.description.trim() || l.sub_lines.some((sl) => sl.description.trim()))
    .map((l, idx) => {
      const subLines: SubLine[] = l.sub_lines
        .filter((sl) => sl.description.trim())
        .map((sl, si) => ({
          id: sl.id,
          type: sl.type,
          description: sl.description,
          qty: parseFloat(sl.qty) || 0,
          unit: sl.unit,
          cost_price: parseFloat(sl.cost_price) || 0,
          markup_pct: parseFloat(sl.markup_pct) || 0,
          unit_price: parseFloat(sl.unit_price) || 0,
          total_cost: subLineCost(sl),
          total_billed: subLineBilled(sl),
          total: subLineBilled(sl),
          taxable: sl.taxable,
          apply_vendor_tax: sl.apply_vendor_tax,
          vendor_tax_rate: sl.vendor_tax_rate,
          qb_tax_code: sl.taxable ? ("TAX" as const) : ("NON" as const),
          qb_item_ref: sl.type === "labor" ? "Installation Labor" : "Job Materials",
          product_service_id: sl.product_service_id || undefined,
          income_account: sl.income_account || undefined,
          cogs_account: sl.cogs_account || undefined,
          sort_order: sl.sort_order,
          quantity_mode: sl.quantity_mode,
          quantity_formula: sl.quantity_formula ?? null,
        }));

      const laborCost = subLines.filter((s) => s.type === "labor").reduce((a, s) => a + s.total_cost, 0);
      const laborBilled = subLines.filter((s) => s.type === "labor").reduce((a, s) => a + s.total_billed, 0);
      const materialCost = subLines.filter((s) => s.type === "material").reduce((a, s) => a + s.total_cost, 0);
      const materialBilled = subLines.filter((s) => s.type === "material").reduce((a, s) => a + s.total_billed, 0);

      return {
        line_num: idx + 1,
        label: l.label || "",
        description: l.description,
        customer_description: l.customer_description || l.description,
        qty: parseFloat(l.qty) || 0,
        unit: l.unit,
        sub_lines: subLines,
        labor_total_cost: laborCost,
        labor_total_billed: laborBilled,
        material_total_cost: materialCost,
        material_total_billed: materialBilled,
        total_cost: laborCost + materialCost,
        total_billed: laborBilled + materialBilled,
        sort_order: l.sort_order,
        group_id: l.group_id ?? null,
      } as LineItem;
    });

  // normalizeEstimateLineItems is called in the mutation layer, so just recalc here
  return raw.map((li) => recalcLineItem(li));
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editEstimate?: Estimate | null;
  defaultCustomerId?: string;
  projectContext?: {
    projectId: string;
    projectName: string;
    projectNumber: string | null;
  };
}

/** Convert an existing Estimate's line items back into draft format for editing */
function estimateToLineDrafts(estimate: Estimate): LineDraft[] {
  const items = estimate.line_items ?? [];
  if (!items.length) return [emptyLine()];
  
  return items.map((item: any, idx: number) => {
    if (isNewFormat(item) || Array.isArray(item.sub_lines)) {
      return {
        id: crypto.randomUUID(),
        label: item.label || "",
        description: item.description || "",
        customer_description: item.customer_description || "",
        qty: String(item.qty ?? 1),
        unit: item.unit || "SF",
        sort_order: item.sort_order ?? idx,
        group_id: item.group_id ?? null,
        sub_lines: (item.sub_lines || []).map((sl: any, si: number): SubLineDraft => ({
          id: sl.id || crypto.randomUUID(),
          type: sl.type || "labor",
          description: sl.description || "",
          qty: String(sl.qty ?? 1),
          unit: sl.unit || "SF",
          cost_price: String(sl.cost_price ?? 0),
          markup_pct: String(sl.markup_pct ?? 0),
          unit_price: String(sl.unit_price ?? 0),
          taxable: sl.taxable ?? (sl.type === "material"),
          apply_vendor_tax: sl.apply_vendor_tax ?? (sl.taxable !== false && sl.type === "material"),
          vendor_tax_rate: sl.vendor_tax_rate ?? 0,
          product_service_id: sl.product_service_id || undefined,
          income_account: sl.income_account || undefined,
          cogs_account: sl.cogs_account || undefined,
          sort_order: sl.sort_order ?? si,
          quantity_mode: (sl.quantity_mode as QuantityMode) ?? "auto",
          quantity_formula: sl.quantity_formula ?? null,
        })),
        expanded: true,
      } as LineDraft;
    }
    // Legacy flat format
    return {
      id: crypto.randomUUID(),
      label: "",
      description: item.description || "",
      customer_description: "",
      qty: String(item.qty ?? 1),
      unit: item.unit || "SF",
      sub_lines: [draftSubLine("labor", String(item.qty ?? 1), item.unit || "SF", item.description || "", 0, "auto")],
      expanded: true,
      sort_order: idx,
    } as LineDraft;
  });
}

// ─── Sortable wrapper components ──────────────────────────────────────

function SortableLineItem({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative" as const,
    zIndex: isDragging ? 50 : "auto" as any,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="flex">
        <button type="button" className="flex items-center px-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground" {...listeners}>
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
}

function SortableSubLineItem({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="flex items-start">
        <button type="button" className="flex items-center px-0.5 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground mt-2" {...listeners}>
          <GripVertical className="h-3 w-3" />
        </button>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────

export const NewEstimateDialog = ({ open, onOpenChange, editEstimate, defaultCustomerId, projectContext }: Props) => {
  
  const { data: templates } = useEstimateTemplates();
  const { data: allProducts } = useProductsServices();
  const { data: estimatorDefaults } = useEstimatorSettings();
  const createEstimate = useCreateEstimate();
  const updateEstimate = useUpdateEstimate();
  const isEditMode = !!editEstimate;
  const [step, setStep] = useState(1);
  const [initialized, setInitialized] = useState(false);

  // Step 1
  const [customerId, setCustomerId] = useState("");
  const [jobType, setJobType] = useState<JobType>("general");
  const [txnDate, setTxnDate] = useState<Date>(new Date());
  const [expiryDate, setExpiryDate] = useState<Date | undefined>();
  const [notes, setNotes] = useState("");
  const [projectName, setProjectName] = useState("");

  // Step 2
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [customerNotes, setCustomerNotes] = useState("");
  const [viewMode, setViewMode] = useState<"internal" | "customer" | "preview">("internal");
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [laborMarkup, setLaborMarkup] = useState("35");
  const [materialMarkup, setMaterialMarkup] = useState("20");
  const [allExpanded, setAllExpanded] = useState(true);

  // Groups
  const [groups, setGroups] = useState<GroupDraft[]>([]);

  // Print settings
  const [printSettings, setPrintSettings] = useState<EstimatePrintSettings>(DEFAULT_PRINT_SETTINGS);

  // Template loading
  const [templatePopoverOpen, setTemplatePopoverOpen] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<EstimateTemplate | null>(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  // Editor full-screen + resizable columns
  const [isFullscreen, setIsFullscreen] = useState<boolean>(() => {
    try { return localStorage.getItem("estimate-editor-fullscreen") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("estimate-editor-fullscreen", isFullscreen ? "1" : "0"); } catch {}
  }, [isFullscreen]);
  const COL_DEFAULTS = useMemo(() => ({
    type: 56,
    product: 170,
    description: 240,
    qty: 72,
    unit: 40,
    cost: 96,
    markup: 72,
    price: 104,
    tax: 104,
    total: 140,
    actions: 26,
  }), []);
  // Auto-fit content widths used by double-click on a column handle and "Auto-fit all".
  const COL_AUTOFIT = useMemo(() => ({
    type: 56,
    product: 170,
    description: 240,
    qty: 72,
    unit: 40,
    cost: 96,
    markup: 72,
    price: 104,
    tax: 104,
    total: 140,
    actions: 26,
  }), []);
  const { widths: colW, getResizeHandle: colHandle, reset: resetCols, autoFitAll } = usePersistentColumnWidths(
    "estimate-editor-col-widths-v11",
    COL_DEFAULTS,
    { minWidth: 36, autoFit: COL_AUTOFIT }
  );

  // Spreadsheet grid track — shared by header and every sub-line row.
  // All columns are fixed-width; a trailing 1fr spacer absorbs any extra
  // viewport width so no single data column stretches unnaturally.
  const gridTemplate = useMemo(
    () =>
      `16px ${colW.type}px ${colW.product}px ${colW.description}px ${colW.qty}px ${colW.unit}px ${colW.cost}px ${colW.markup}px ${colW.price}px ${colW.tax}px ${colW.total}px 1fr ${colW.actions}px`,
    [colW]
  );

  // Draft persistence
  const draftKey = isEditMode ? `estimate_edit_${editEstimate?.id}` : "estimate_new";
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<any>(draftKey);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Auto-save draft on meaningful state changes
  useEffect(() => {
    if (!initialized || isEditMode) return;
    saveDraft({
      step, customerId, jobType,
      txnDate: txnDate.toISOString(),
      expiryDate: expiryDate?.toISOString() ?? null,
      notes, lines, customerNotes, laborMarkup, materialMarkup,
      groups, printSettings,
    });
  }, [initialized, isEditMode, step, customerId, jobType, txnDate, expiryDate, notes, lines, customerNotes, laborMarkup, materialMarkup, groups, printSettings, saveDraft]);

  // Pre-fill from editEstimate when opening in edit mode — re-hydrate when
  // the source estimate id or its updated_at changes so we never render stale
  // tax rates / line totals after a background refetch.
  useEffect(() => {
    if (!open || !editEstimate) return;
    setCustomerId(editEstimate.customer_id || "");
    setJobType(editEstimate.job_type || "general");
    setTxnDate(new Date(editEstimate.txn_date));
    setExpiryDate(editEstimate.expiry_date ? new Date(editEstimate.expiry_date) : undefined);
    setNotes(editEstimate.notes || "");
    setProjectName((editEstimate as any).project_name || "");
    setLines(estimateToLineDrafts(editEstimate));
    setGroups((editEstimate.estimate_groups ?? []).map((g: any) => ({
      ...g,
      collapsed: false,
    })));
    setPrintSettings(editEstimate.print_settings ?? DEFAULT_PRINT_SETTINGS);
    if ((editEstimate as any).tax_rate != null) setTaxRate((editEstimate as any).tax_rate);
    setStep(2);
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editEstimate?.id, (editEstimate as any)?.updated_at]);

  if (open && !editEstimate && !initialized) {
    // Try to restore a saved draft first
    const draft = loadDraft();
    if (draft && draft.lines?.length > 0) {
      setCustomerId(draft.customerId || defaultCustomerId || "");
      setJobType(draft.jobType || "general");
      setTxnDate(draft.txnDate ? new Date(draft.txnDate) : new Date());
      setExpiryDate(draft.expiryDate ? new Date(draft.expiryDate) : undefined);
      setNotes(draft.notes || "");
      setLines(draft.lines);
      setCustomerNotes(draft.customerNotes || "");
      setLaborMarkup(draft.laborMarkup || "35");
      setMaterialMarkup(draft.materialMarkup || "20");
      setGroups(draft.groups || []);
      setPrintSettings(draft.printSettings || DEFAULT_PRINT_SETTINGS);
      setStep(draft.step || 1);
      toast.info("Restored your previous draft");
    } else {
      if (defaultCustomerId) setCustomerId(defaultCustomerId);
      if (projectContext) {
        const projLabel = projectContext.projectNumber
          ? `${projectContext.projectName} (${projectContext.projectNumber})`
          : projectContext.projectName;
        setNotes(`CO for Project: ${projLabel}`);
      }
      // Apply company defaults for new estimates
      if (estimatorDefaults) {
        setLaborMarkup(String(estimatorDefaults.default_labor_markup_pct));
        setMaterialMarkup(String(estimatorDefaults.default_material_markup_pct));
      }
    }
    setInitialized(true);
  }
  if (!open && initialized) {
    setInitialized(false);
  }

  // ============================================
  // SMART QUANTITY MAPPING — Live Reactive Engine
  // ============================================
  const overriddenMappings = useRef<Set<string>>(new Set());

  const applySmartMappings = useCallback((currentLines: LineDraft[]): LineDraft[] => {
    if (!allProducts?.length) return currentLines;

    const codeQtyMap = new Map<string, number>();
    currentLines.forEach(l => {
      l.sub_lines.forEach(sl => {
        if (!sl.product_service_id) return;
        const product = allProducts.find(p => p.id === sl.product_service_id);
        if (product) {
          const existing = codeQtyMap.get(product.code) || 0;
          codeQtyMap.set(product.code, existing + (parseFloat(sl.qty) || 0));
        }
      });
    });

    let changed = false;
    const newLines = currentLines.map(l => ({
      ...l,
      sub_lines: l.sub_lines.map(sl => {
        if (!sl.product_service_id) return sl;
        if (overriddenMappings.current.has(sl.id)) return sl;

        // Keep parent/formula-driven quantities intact in create/edit flows.
        const mode = (sl.quantity_mode ?? "auto") as QuantityMode;
        if (mode !== "manual") return sl;

        const product = allProducts.find(p => p.id === sl.product_service_id);
        if (!product?.qty_mapping || !product.qty_mapping.auto_apply) return sl;

        const mapping = product.qty_mapping;
        let calculatedQty: number | null = null;
        let calculatedCost: number | null = null;

        if (mapping.type === 'ratio' && mapping.links?.length) {
          const link = mapping.links[0];
          const sourceQty = codeQtyMap.get(link.product_code) || 0;
          if (sourceQty === 0) return sl;

          let result = sourceQty;
          if (link.operation === 'divide') result = sourceQty / (link.value || 1);
          else if (link.operation === 'multiply') result = sourceQty * (link.value || 1);

          if (link.round === 'ceil') result = Math.ceil(result);
          else if (link.round === 'floor') result = Math.floor(result);
          else if (link.round === 'round') result = Math.round(result);

          calculatedQty = Math.max(1, result);
        } else if (mapping.type === 'sum_multiply' && mapping.links?.length) {
          let sum = 0;
          mapping.links.forEach((link: any) => {
            const linkQty = codeQtyMap.get(link.product_code) || 0;
            if (link.operation === 'add') sum += linkQty;
          });
          if (sum === 0) return sl;
          if (mapping.use_as === 'cost_price' && mapping.then_multiply) {
            calculatedCost = sum * mapping.then_multiply;
            calculatedQty = sum;
          } else {
            calculatedQty = sum;
          }
        }

        if (calculatedQty !== null && String(calculatedQty) !== sl.qty) {
          changed = true;
          const updatedSl = { ...sl, qty: String(calculatedQty) };
          if (calculatedCost !== null) {
            updatedSl.cost_price = String(calculatedCost / calculatedQty);
            updatedSl.unit_price = String(calcUnitPrice(updatedSl.cost_price, updatedSl.markup_pct, updatedSl.taxable, updatedSl.apply_vendor_tax, updatedSl.vendor_tax_rate));
          }
          return updatedSl;
        }
        if (calculatedCost !== null) {
          const costPerUnit = calculatedQty ? calculatedCost / calculatedQty : 0;
          if (String(costPerUnit) !== sl.cost_price) {
            changed = true;
            return {
              ...sl,
              cost_price: String(costPerUnit),
              unit_price: String(calcUnitPrice(String(costPerUnit), sl.markup_pct, sl.taxable, sl.apply_vendor_tax, sl.vendor_tax_rate)),
            };
          }
        }
        return sl;
      }),
    }));

    return changed ? newLines : currentLines;
  }, [allProducts]);

  // Run smart mappings whenever lines change
  const prevLinesRef = useRef<string>("");
  useEffect(() => {
    const serialized = JSON.stringify(lines.map(l => l.sub_lines.map(sl => ({ id: sl.id, qty: sl.qty, pid: sl.product_service_id }))));
    if (serialized === prevLinesRef.current) return;
    prevLinesRef.current = serialized;

    const updated = applySmartMappings(lines);
    if (updated !== lines) {
      setLines(updated);
    }
  }, [lines, applySmartMappings]);

  // Repair pass: existing estimates have sub-line descriptions saved as numeric qty values
  // (e.g. "2005", "1135") instead of product names. When a product is linked, replace the
  // wrong text with `${product.code} ${product.name}`. Real text edits (containing letters)
  // are preserved.
  const repairedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!allProducts?.length || !lines.length) return;
    let mutated = false;
    const next = lines.map((l) => {
      const parentQty = String(l.qty ?? "").trim();
      const newSubs = l.sub_lines.map((sl) => {
        if (!sl.product_service_id) return sl;
        const desc = (sl.description ?? "").trim();
        const isBlank = desc === "";
        const isNumericOnly = desc !== "" && /^[\d.,\s]+$/.test(desc);
        const equalsParentQty = parentQty !== "" && desc === parentQty;
        if (!isBlank && !isNumericOnly && !equalsParentQty) return sl;
        const product = allProducts.find((p) => p.id === sl.product_service_id);
        if (!product) return sl;
        const correct = product.code ? `${product.code} ${product.name}` : product.name;
        if (desc === correct) return sl;
        const key = `${sl.id}:${correct}`;
        if (repairedRef.current.has(key)) return sl;
        repairedRef.current.add(key);
        mutated = true;
        return { ...sl, description: correct };
      });
      if (newSubs.some((s, i) => s !== l.sub_lines[i])) {
        return { ...l, sub_lines: newSubs };
      }
      return l;
    });
    if (mutated) setLines(next);
  }, [lines, allProducts]);


  const updateLine = (idx: number, field: keyof LineDraft, val: any) => {
    setLines((prev) => {
      const updated = prev.map((l, i) => (i === idx ? { ...l, [field]: val } : l));
      // If qty changed, re-normalize that line for auto-propagation
      if (field === "qty") {
        return updated.map((l, i) => (i === idx ? normalizeDraftLine(l) : l));
      }
      return updated;
    });
  };

  const toggleExpand = (idx: number) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, expanded: !l.expanded } : l)));
  };

  const addSubLine = (lineIdx: number, type: "labor" | "material") => {
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== lineIdx) return l;
        const newSl = draftSubLine(type, l.qty, l.unit, l.description, l.sub_lines.length, "auto");
        // Apply current taxRate to new material sub-lines
        if (type === "material" && taxRate > 0) {
          newSl.vendor_tax_rate = taxRate;
          newSl.unit_price = String(calcUnitPrice(newSl.cost_price, newSl.markup_pct, newSl.taxable, newSl.apply_vendor_tax, taxRate));
        }
        const updatedLine = { ...l, sub_lines: [...l.sub_lines, newSl] };
        return normalizeDraftLine(updatedLine);
      })
    );
  };

  const updateSubLine = (lineIdx: number, subIdx: number, field: keyof SubLineDraft, val: string | boolean) => {
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== lineIdx) return l;
        const newSubs = l.sub_lines.map((sl, si) => {
          if (si !== subIdx) return sl;
          const updated = { ...sl, [field]: val };
          // Track manual qty override for mapped products
          if (field === "qty") {
            updated.quantity_mode = "manual";
            if (sl.product_service_id) {
              const prod = allProducts?.find(p => p.id === sl.product_service_id);
              if (prod?.qty_mapping?.auto_apply) {
                overriddenMappings.current.add(sl.id);
              }
            }
          }
          if (field === "cost_price" || field === "markup_pct") {
            const newCost = field === "cost_price" ? val as string : sl.cost_price;
            const newMarkup = field === "markup_pct" ? val as string : sl.markup_pct;
            updated.unit_price = String(calcUnitPrice(newCost, newMarkup, updated.taxable, updated.apply_vendor_tax, updated.vendor_tax_rate));
          }
          if (field === "unit_price") {
            // When user manually sets unit_price, back-calculate markup from effective cost
            const c = parseFloat(sl.cost_price) || 0;
            if (c > 0) {
              const price = parseFloat(val as string) || 0;
              const t = updated.vendor_tax_rate / 100;
              const effectiveCost = updated.taxable && updated.apply_vendor_tax ? c * (1 + t) : c;
              // If taxable+vendor: unit_price = effectiveCost * (1+m/100) * (1+t), solve for m
              const divisor = updated.taxable && updated.apply_vendor_tax ? effectiveCost * (1 + t) : effectiveCost;
              if (divisor > 0) {
                updated.markup_pct = String(Math.round(((price / divisor) - 1) * 100 * 100) / 100);
              }
            }
          }
          if (field === "unit") {
            const triggerUnit = getLineTriggerUnitDraft(l);
            if (val === triggerUnit) {
              updated.quantity_mode = "auto";
              updated.qty = l.qty;
            } else {
              updated.quantity_mode = "manual";
            }
            if (val === "LS") {
              updated.qty = "1";
              updated.quantity_mode = "manual";
            }
          }
          if (field === "taxable") {
            updated.taxable = val as boolean;
            // Recalc unit_price with new taxable state
            updated.unit_price = String(calcUnitPrice(updated.cost_price, updated.markup_pct, updated.taxable, updated.apply_vendor_tax, updated.vendor_tax_rate));
          }
          if (field === "apply_vendor_tax") {
            updated.apply_vendor_tax = val as boolean;
            // Recalc unit_price with new vendor tax state
            updated.unit_price = String(calcUnitPrice(updated.cost_price, updated.markup_pct, updated.taxable, updated.apply_vendor_tax, updated.vendor_tax_rate));
          }
          return updated;
        });
        const updatedLine = { ...l, sub_lines: newSubs };
        // If a unit changed on a labor sub-line that might be the trigger, re-normalize
        if (field === "unit") {
          return normalizeDraftLine(updatedLine);
        }
        return updatedLine;
      })
    );
  };

  const toggleQuantityMode = (lineIdx: number, subIdx: number) => {
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== lineIdx) return l;
        const newSubs = l.sub_lines.map((sl, si) => {
          if (si !== subIdx) return sl;
          if (sl.quantity_mode === "auto") {
            // auto => manual — preserve current qty
            return { ...sl, quantity_mode: "manual" as QuantityMode };
          } else if (sl.quantity_mode === "manual") {
            // manual => formula — initialize blank formula
            const parentUnit = l.unit;
            const defaultFormula: QuantityFormula = {
              kind: "coverage",
              source: "parent_qty",
              source_unit: parentUnit,
              coverage_per_unit: 1,
              round_mode: "up",
            };
            const computed = applyFormula(parseFloat(l.qty) || 0, parentUnit, defaultFormula);
            return { ...sl, quantity_mode: "formula" as QuantityMode, quantity_formula: defaultFormula, qty: String(computed) };
          } else {
            // formula => auto — re-normalize
            const triggerUnit = getLineTriggerUnitDraft(l);
            const newQty = triggerUnit && sl.unit === triggerUnit ? l.qty : "0";
            return { ...sl, quantity_mode: "auto" as QuantityMode, qty: newQty, quantity_formula: null };
          }
        });
        return { ...l, sub_lines: newSubs };
      })
    );
  };

  const updateFormula = (lineIdx: number, subIdx: number, formula: QuantityFormula) => {
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== lineIdx) return l;
        const newSubs = l.sub_lines.map((sl, si) => {
          if (si !== subIdx) return sl;
          const parentQty = parseFloat(l.qty) || 0;
          const computed = applyFormula(parentQty, l.unit, formula);
          return { ...sl, quantity_formula: formula, qty: String(computed) };
        });
        return { ...l, sub_lines: newSubs };
      })
    );
  };

  const removeSubLine = (lineIdx: number, subIdx: number) => {
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== lineIdx) return l;
        const newSubs = l.sub_lines.filter((_, si) => si !== subIdx).map((sl, si) => ({ ...sl, sort_order: si }));
        return normalizeDraftLine({ ...l, sub_lines: newSubs });
      })
    );
  };

  const selectProduct = (lineIdx: number, subIdx: number, productId: string) => {
    const product = allProducts?.find((p) => p.id === productId);
    if (!product) return;
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== lineIdx) return l;
        const newSubs = l.sub_lines.map((sl, si) => {
          if (si !== subIdx) return sl;
          const triggerUnit = getLineTriggerUnitDraft(l);
          const newUnit = product.unit;
          let newMode = sl.quantity_mode;
          let newQty = sl.qty;
          if (triggerUnit && newUnit === triggerUnit) {
            newMode = "auto";
            newQty = l.qty;
          } else if (triggerUnit && newUnit !== triggerUnit) {
            newMode = "manual";
          }
          const effectiveVtr = (product.is_taxable && sl.apply_vendor_tax && taxRate > 0) ? taxRate : sl.vendor_tax_rate;
          return {
            ...sl,
            description: product.code ? `${product.code} ${product.name}` : product.name,
            unit: newUnit,
            markup_pct: String(product.default_markup_pct),
            taxable: product.is_taxable,
            product_service_id: product.id,
            income_account: product.income_account || undefined,
            cogs_account: product.cogs_account || undefined,
            vendor_tax_rate: effectiveVtr,
            unit_price: String(calcUnitPrice(sl.cost_price, String(product.default_markup_pct), product.is_taxable, sl.apply_vendor_tax, effectiveVtr)),
            quantity_mode: newMode,
            qty: newQty,
          };
        });
        return normalizeDraftLine({ ...l, sub_lines: newSubs });
      })
    );
  };

  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, sort_order: i })));

  const moveLine = (idx: number, dir: -1 | 1) => {
    setLines((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((l, i) => ({ ...l, sort_order: i }));
    });
  };

  const duplicateLine = (idx: number) => {
    setLines((prev) => {
      const src = prev[idx];
      const dup: LineDraft = normalizeDraftLine({
        id: crypto.randomUUID(),
        label: src.label,
        description: src.description,
        customer_description: src.customer_description,
        qty: "1",
        unit: src.unit,
        sub_lines: src.sub_lines.map((sl, si) => ({
          ...sl,
          id: crypto.randomUUID(),
          qty: "1",
          cost_price: "0",
          markup_pct: sl.markup_pct,
          unit_price: "0",
          sort_order: si,
          quantity_mode: "auto" as QuantityMode,
        })),
        expanded: true,
        sort_order: idx + 1,
      });
      const next = [...prev];
      next.splice(idx + 1, 0, dup);
      return next.map((l, i) => ({ ...l, sort_order: i }));
    });
  };

  const applyMasterMarkup = () => {
    setLines((prev) =>
      prev.map((l) => ({
        ...l,
        sub_lines: l.sub_lines.map((sl) => {
          const m = sl.type === "labor" ? laborMarkup : materialMarkup;
          return { ...sl, markup_pct: m, unit_price: String(calcUnitPrice(sl.cost_price, m, sl.taxable, sl.apply_vendor_tax, sl.vendor_tax_rate)) };
        }),
      }))
    );
    toast.success(`Applied Labor ${laborMarkup}% / Material ${materialMarkup}% markup`);
  };

  const populateFromTakeoff = async (estimateId: string, currentLines: LineDraft[]): Promise<LineDraft[]> => {
    try {
      // Fetch annotations for this estimate
      const { data: annotations } = await (supabase as any)
        .from("document_annotations")
        .select("annotation_type, measured_value, unit, takeoff_group_id")
        .eq("estimate_id", estimateId) as { data: { annotation_type: string; measured_value: number | null; unit: string | null; takeoff_group_id: string | null }[] | null };
      if (!annotations?.length) return currentLines;

      // Fetch takeoff groups for name matching
      const { data: takeoffGroups } = await (supabase as any)
        .from("takeoff_groups")
        .select("id, name")
        .eq("estimate_id", estimateId) as { data: { id: string; name: string | null }[] | null };

      const groupNameMap = new Map((takeoffGroups ?? []).map((g: any) => [g.name?.toLowerCase(), g.id]));

      return currentLines.map((line) => {
        // Check if this line's parent qty should come from takeoff
        // Only populate lines that still have default qty "1" and no cost entered
        const hasData = line.sub_lines.some((sl) => parseFloat(sl.cost_price) > 0);
        if (hasData) return line;

        // Match measurement type by unit convention
        let totalMeasurement = 0;
        const unitLower = line.unit.toLowerCase();
        const isArea = ["sf", "sq"].includes(unitLower);
        const isLinear = ["lf", "ft", "in"].includes(unitLower);
        const isCount = ["ea"].includes(unitLower);

        const type = isArea ? "measurement_area" : isLinear ? "measurement_linear" : isCount ? "measurement_count" : null;
        if (!type) return line;

        const matchingAnnotations = annotations.filter((a: any) => a.annotation_type === type);
        matchingAnnotations.forEach((a: any) => {
          totalMeasurement += a.measured_value ?? 0;
        });

        if (totalMeasurement > 0 && line.qty === "1") {
          const rounded = Math.round(totalMeasurement * 100) / 100;
          return normalizeDraftLine({ ...line, qty: String(rounded) });
        }
        return line;
      });
    } catch {
      return currentLines;
    }
  };

  const toggleAllExpanded = () => {
    const next = !allExpanded;
    setAllExpanded(next);
    setLines((prev) => prev.map((l) => ({ ...l, expanded: next })));
  };

  const handleLoadTemplate = (tpl: EstimateTemplate) => {
    const hasContent = lines.some((l) => l.description.trim() || l.sub_lines.some((sl) => sl.description.trim()));
    if (hasContent) {
      setPendingTemplate(tpl);
    } else {
      setLines(templateToLines(tpl));
      setLaborMarkup(String(tpl.labor_markup_pct));
      setMaterialMarkup(String(tpl.material_markup_pct));
      if (tpl.template_groups?.length) {
        setGroups(tpl.template_groups.map((g) => ({ ...g, collapsed: false, color: g.color || GROUP_COLORS[0] })));
      }
    }
    setTemplatePopoverOpen(false);
  };

  const applyTemplate = (mode: "replace" | "append") => {
    if (!pendingTemplate) return;
    const tplLines = templateToLines(pendingTemplate);
    if (mode === "replace") {
      setLines(tplLines);
      if (pendingTemplate.template_groups?.length) {
        setGroups(pendingTemplate.template_groups.map((g) => ({ ...g, collapsed: false, color: g.color || GROUP_COLORS[0] })));
      }
    } else {
      setLines((prev) => normalizeDraftLines([...prev, ...tplLines]));
    }
    setLaborMarkup(String(pendingTemplate.labor_markup_pct));
    setMaterialMarkup(String(pendingTemplate.material_markup_pct));
    setPendingTemplate(null);
  };

  // ─── DnD handlers ────────────────────────────────────────────

  const handleLineDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setLines((prev) => {
      const oldIndex = prev.findIndex((l) => l.id === active.id);
      const newIndex = prev.findIndex((l) => l.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex).map((l, i) => ({ ...l, sort_order: i }));
    });
  };

  const handleSubLineDragEnd = (lineIdx: number) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== lineIdx) return l;
        const oldIndex = l.sub_lines.findIndex((sl) => sl.id === active.id);
        const newIndex = l.sub_lines.findIndex((sl) => sl.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return l;
        const reordered = arrayMove(l.sub_lines, oldIndex, newIndex).map((sl, si) => ({ ...sl, sort_order: si }));
        return normalizeDraftLine({ ...l, sub_lines: reordered });
      })
    );
  };

  // ─── Group management ───────────────────────────────────────

  const addGroup = () => {
    const newGroup: GroupDraft = {
      id: crypto.randomUUID(),
      name: "",
      group_type: "custom",
      sort_order: groups.length,
      color: GROUP_COLORS[groups.length % GROUP_COLORS.length],
      collapsed: false,
    };
    setGroups((prev) => [...prev, newGroup]);
  };

  const updateGroup = (groupId: string, field: keyof GroupDraft, val: any) => {
    setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, [field]: val } : g)));
  };

  const removeGroup = (groupId: string) => {
    setGroups((prev) => prev.filter((g) => g.id !== groupId).map((g, i) => ({ ...g, sort_order: i })));
    // Ungroup any lines that were in this group
    setLines((prev) => prev.map((l) => (l.group_id === groupId ? { ...l, group_id: null } : l)));
  };

  const addLineToGroup = (groupId: string | null) => {
    setLines((prev) => [...prev, emptyLine(prev.length, groupId)]);
  };

  const moveLineToGroup = (lineId: string, groupId: string | null) => {
    setLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, group_id: groupId } : l)));
  };

  /** Lines organized by group for rendering */
  const groupedLines = useMemo(() => {
    if (groups.length === 0) return [{ group: null as GroupDraft | null, lines }];
    const sorted = [...groups].sort((a, b) => a.sort_order - b.sort_order);
    const result: { group: GroupDraft | null; lines: LineDraft[] }[] = sorted.map((g) => ({
      group: g,
      lines: lines.filter((l) => l.group_id === g.id),
    }));
    const ungrouped = lines.filter((l) => !l.group_id || !groups.some((g) => g.id === l.group_id));
    if (ungrouped.length > 0 || groups.length === 0) {
      result.push({ group: null, lines: ungrouped });
    }
    return result;
  }, [lines, groups]);

  const groupSubtotal = (groupLines: LineDraft[]) =>
    groupLines.reduce((s, l) => s + calcLineTotal(l), 0);

  // Fetch customer name + address + contact for preview
  const [customerName, setCustomerName] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [customerState, setCustomerState] = useState("");
  const [customerContactName, setCustomerContactName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  useEffect(() => {
    if (!customerId) { setCustomerName(""); setCustomerAddress(""); setCustomerState(""); setCustomerContactName(""); setCustomerPhone(""); setCustomerEmail(""); return; }
    (async () => {
      const { data } = await supabase.from("customers").select("name, contact_name, email, phone, billing_address, billing_city, billing_state, billing_zip").eq("id", customerId).maybeSingle();
      if (data) {
        setCustomerName((data as any).name ?? "");
        setCustomerContactName((data as any).contact_name ?? "");
        setCustomerPhone((data as any).phone ?? "");
        setCustomerEmail((data as any).email ?? "");
        const parts = [(data as any).billing_address, (data as any).billing_city, (data as any).billing_state, (data as any).billing_zip].filter(Boolean);
        setCustomerAddress(parts.join(", "));
        setCustomerState((data as any).billing_state ?? "");
      }
    })();
  }, [customerId]);

  // Proposal presets
  const { data: proposalPresets = [] } = useProposalPresets();
  const activePreset = useMemo(() => findPresetForJobType(proposalPresets, jobType), [proposalPresets, jobType]);

  // Fetch company info + tax rate for preview and tax calc
  // Default to company-configured sales tax rate (8.25% for Texas) until state resolves
  const [taxRate, setTaxRate] = useState(estimatorDefaults?.default_sales_tax_rate ?? 8.25);
  const [taxState, setTaxState] = useState(""); // selected state for tax lookup
  const [companyInfo, setCompanyInfo] = useState<{ name: string; address?: string; city?: string; state?: string; zip?: string; phone?: string; logo_url?: string; website?: string } | null>(null);

  const lookupTaxForState = useCallback(async (stateCode: string) => {
    if (!stateCode) return;
    const { data: tr } = await supabase.from("state_tax_rates" as any).select("base_rate").eq("state_code", stateCode.toUpperCase()).maybeSingle();
    if (tr) setTaxRate(((tr as any).base_rate ?? 0) * 100);
  }, []);

  useEffect(() => {
    (async () => {
      const { data: cid } = await supabase.rpc("my_company_id" as any);
      if (!cid) return;
      const { data: co } = await supabase.from("companies").select("name, address, city, state, zip, phone, logo_url, website, material_purchase_tax_rate").eq("id", cid).maybeSingle();
      if (co) setCompanyInfo(co as any);
      if (!co?.state) return;
      setTaxState(co.state.toUpperCase());
      lookupTaxForState(co.state.toUpperCase());
    })();
  }, [lookupTaxForState]);

  // Tax state — prefer customer state, fall back to company state
  useEffect(() => {
    const rawState = customerState || companyInfo?.state;
    if (!rawState) return; // keep current default (8.25%) if no state available
    const stateCode = rawState.toUpperCase();
    setTaxState(stateCode);
    lookupTaxForState(stateCode);
  }, [customerState, companyInfo?.state, lookupTaxForState]);

  // When taxRate changes or sub-lines are added, ensure all material sub-lines have the correct vendor_tax_rate
  const subLineCount = lines.reduce((sum, l) => sum + l.sub_lines.length, 0);

  useEffect(() => {
    if (taxRate <= 0) return;
    setLines((prev) => {
      let changed = false;
      const updated = prev.map((l) => ({
        ...l,
        sub_lines: l.sub_lines.map((sl) => {
          if (sl.type === "material" && sl.apply_vendor_tax && sl.vendor_tax_rate !== taxRate) {
            changed = true;
            return {
              ...sl,
              vendor_tax_rate: taxRate,
              unit_price: String(calcUnitPrice(sl.cost_price, sl.markup_pct, sl.taxable, sl.apply_vendor_tax, taxRate)),
            };
          }
          return sl;
        }),
      }));
      return changed ? updated : prev;
    });
  }, [taxRate, subLineCount]);

  // Auto-enable Labor/Material Breakdown print setting when tax rate is 8.25%
  useEffect(() => {
    if (taxRate === 8.25) {
      setPrintSettings((prev) =>
        prev.show_labor_material_breakdown ? prev : { ...prev, show_labor_material_breakdown: true }
      );
    }
  }, [taxRate]);

  const totals = useMemo(() => {
    let laborCost = 0, laborBilled = 0, materialCost = 0, materialBilled = 0;
    let embeddedVendorTax = 0, embeddedCustomerTax = 0;
    lines.forEach((l) => {
      l.sub_lines.forEach((sl) => {
        const c = subLineCost(sl);
        const b = subLineBilled(sl);
        if (sl.type === "labor") { laborCost += c; laborBilled += b; }
        else {
          materialCost += c;
          materialBilled += b;
          // Compute embedded taxes for transparency display
          if (sl.taxable && sl.apply_vendor_tax && sl.vendor_tax_rate > 0) {
            const rawCost = parseFloat(sl.cost_price) || 0;
            const qty = parseFloat(sl.qty) || 0;
            const vt = sl.vendor_tax_rate / 100;
            const effectiveCost = rawCost * (1 + vt);
            const preTaxPrice = effectiveCost * (1 + (parseFloat(sl.markup_pct) || 0) / 100);
            const unitPrice = preTaxPrice * (1 + vt);
            embeddedVendorTax += (effectiveCost - rawCost) * qty;
            embeddedCustomerTax += (unitPrice - preTaxPrice) * qty;
          }
        }
      });
    });
    // Since tax is now embedded in unit_price, totalBilled already includes tax
    const landedMaterialCost = materialCost + embeddedVendorTax;
    const totalCost = laborCost + landedMaterialCost;
    const totalBilled = laborBilled + materialBilled;
    const totalWithTax = totalBilled; // tax is embedded
    const profit = totalBilled - totalCost;
    const margin = totalBilled > 0 ? (profit / totalBilled) * 100 : 0;
    const laborMargin = laborBilled > 0 ? ((laborBilled - laborCost) / laborBilled) * 100 : 0;
    const materialMargin = materialBilled > 0 ? ((materialBilled - landedMaterialCost) / materialBilled) * 100 : 0;
    return {
      laborCost, laborBilled, laborMargin,
      materialCost, materialBilled, materialMargin,
      vendorTaxOnMaterials: embeddedVendorTax,
      landedMaterialCost,
      embeddedCustomerTax,
      totalCost, totalBilled,
      taxAmount: 0, // no separate tax line — embedded in unit prices
      taxRate,
      totalWithTax,
      profit, margin,
    };
  }, [lines, taxRate, companyInfo]);

  const buildPrintData = useMemo((): EstimatePrintData => {
    const toPrintLine = (line: LineDraft, idx: number): EstimatePrintLineItem => {
      const qty = parseFloat(line.qty) || 0;
      const lineTotal = line.sub_lines.reduce((s, sl) => s + subLineBilled(sl), 0);
      const laborTotal = line.sub_lines.filter(sl => sl.type === "labor").reduce((s, sl) => s + subLineBilled(sl), 0);
      const materialTotal = line.sub_lines.filter(sl => sl.type === "material").reduce((s, sl) => s + subLineBilled(sl), 0);
      return {
        line_number: idx + 1,
        label: line.label || undefined,
        description: line.description,
        customer_description: line.customer_description || undefined,
        quantity: qty,
        unit: line.unit,
        labor_unit_price: qty > 0 ? laborTotal / qty : 0,
        material_unit_price: qty > 0 ? materialTotal / qty : 0,
        unit_price: qty > 0 ? lineTotal / qty : 0,
        amount: lineTotal,
        sub_lines: line.sub_lines.map(sl => ({ type: sl.type, description: sl.description })),
      };
    };

    // Build groups, filtering out empty ones
    const printGroups: EstimatePrintGroup[] = groups.length > 0
      ? groups.map(g => {
          const groupLines = lines.filter(l => l.group_id === g.id);
          return {
            name: g.name,
            color: g.color,
            lines: groupLines.map((l, i) => toPrintLine(l, i)),
            subtotal: groupLines.reduce((s, l) => s + calcLineTotal(l), 0),
          };
        }).filter(g => g.lines.length > 0)
      : [];

    const ungroupedLines = lines.filter(l => !l.group_id || !groups.find(g => g.id === l.group_id));

    // Determine flat_lines: ungrouped lines always, or ALL lines if no groups
    const flatLines = ungroupedLines.length > 0
      ? ungroupedLines.map((l, i) => toPrintLine(l, i))
      : (printGroups.length === 0 && lines.length > 0 ? lines.map((l, i) => toPrintLine(l, i)) : undefined);

    // Resolve preset data for clarifications/exclusions/acceptance
    const presetClarifications = activePreset?.clarifications ?? [];
    const presetExclusions = activePreset?.exclusions ?? [];
    const presetAcceptance = activePreset?.acceptance_text ?? undefined;

    return {
      estimate_number: editEstimate?.estimate_number || "DRAFT",
      estimate_date: txnDate.toISOString(),
      expiry_date: expiryDate?.toISOString() ?? null,
      status: "Draft",
      customer_name: customerName || undefined,
      customer_contact_name: customerContactName || undefined,
      customer_phone: customerPhone || undefined,
      customer_email: customerEmail || undefined,
      project_name: projectName || undefined,
      project_address: customerAddress || undefined,
      company_name: companyInfo?.name,
      company_logo_url: companyInfo?.logo_url,
      company_address: companyInfo?.address,
      company_city_state_zip: [companyInfo?.city, companyInfo?.state, companyInfo?.zip].filter(Boolean).join(", ") || undefined,
      company_phone: companyInfo?.phone,
      company_website: companyInfo?.website,
      groups: printGroups.length > 0 ? printGroups : undefined,
      flat_lines: flatLines,
      subtotal: totals.totalBilled,
      tax_amount: totals.taxAmount,
      tax_rate: totals.taxRate,
      total: totals.totalWithTax > totals.totalBilled ? totals.totalWithTax : totals.totalBilled,
      labor_subtotal: totals.laborBilled,
      material_subtotal_pre_tax: Math.max(totals.materialBilled - totals.embeddedCustomerTax, 0),
      sales_tax_total: totals.embeddedCustomerTax > 0 ? totals.embeddedCustomerTax : totals.taxAmount,
      notes: customerNotes || undefined,
      settings: printSettings,
      clarifications: presetClarifications.length > 0 ? presetClarifications : undefined,
      exclusions: presetExclusions.length > 0 ? presetExclusions : undefined,
      acceptance_text: presetAcceptance,
    };
    
  }, [lines, groups, totals, printSettings, customerName, customerContactName, customerPhone, customerEmail, customerAddress, projectName, companyInfo, txnDate, expiryDate, customerNotes, editEstimate, activePreset]);

  const reset = () => {
    setStep(1);
    setCustomerId("");
    setJobType("general");
    setTxnDate(new Date());
    setExpiryDate(undefined);
    setNotes("");
    setProjectName("");
    setLines([emptyLine()]);
    setCustomerNotes("");
    setViewMode("internal");
    setLaborMarkup(String(estimatorDefaults?.default_labor_markup_pct ?? 35));
    setMaterialMarkup(String(estimatorDefaults?.default_material_markup_pct ?? 20));
    setAllExpanded(true);
    setGroups([]);
    setPrintSettings(DEFAULT_PRINT_SETTINGS);
    clearDraft();
  };

  const handleSave = async (syncStatus: string) => {
    // Validate sub-lines have required data
    for (const line of lines) {
      if (!line.description.trim()) continue; // skip empty parent lines
      for (const sl of line.sub_lines) {
        if (!sl.description.trim()) {
          return toast.error(`Line "${line.description}" has a sub-line with no description`);
        }
        if (Number(sl.cost_price) <= 0) {
          return toast.error(`"${sl.description}" on "${line.description}" requires a cost price`);
        }
        if (Number(sl.unit_price) <= 0) {
          return toast.error(`"${sl.description}" on "${line.description}" requires a unit price`);
        }
      }
    }

    const lineItems = draftsToLineItems(lines);
    const persistGroups: EstimateGroup[] = groups.map((g) => ({
      id: g.id,
      name: g.name,
      group_type: g.group_type,
      sort_order: g.sort_order,
      color: g.color,
    }));
    try {
      if (isEditMode && editEstimate) {
        await updateEstimate.mutateAsync({
          id: editEstimate.id,
          customer_id: customerId || null,
          txn_date: format(txnDate, "yyyy-MM-dd"),
          expiry_date: expiryDate ? format(expiryDate, "yyyy-MM-dd") : null,
          notes: customerNotes || notes || null,
          line_items: lineItems,
          estimate_groups: persistGroups,
          print_settings: printSettings,
          subtotal: totals.totalBilled,
          tax_rate: totals.taxRate,
          tax_amount: totals.taxAmount,
          total_amount: totals.totalWithTax,
          qb_sync_status: syncStatus,
          job_type: jobType,
          project_name: projectName || null,
        });
        toast.success("Estimate updated");
      } else {
        await createEstimate.mutateAsync({
          customer_id: customerId || null,
          txn_date: format(txnDate, "yyyy-MM-dd"),
          expiry_date: expiryDate ? format(expiryDate, "yyyy-MM-dd") : null,
          notes: customerNotes || notes || null,
          line_items: lineItems,
          estimate_groups: persistGroups,
          print_settings: printSettings,
          subtotal: totals.totalBilled,
          tax_rate: totals.taxRate,
          tax_amount: totals.taxAmount,
          total_amount: totals.totalWithTax,
          qb_sync_status: syncStatus,
          job_type: jobType,
          project_name: projectName || null,
          project_id: projectContext?.projectId || null,
          is_change_order: !!projectContext,
        });
        toast.success(syncStatus === "pending" ? "Saved — QB sync coming soon" : "Estimate saved as draft");
      }
      reset();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to save estimate");
    }
  };

  const isSaving = createEstimate.isPending || updateEstimate.isPending;

  const canNext1 = !!customerId && !!jobType;
  const hasLines = lines.some((l) => l.description.trim() || l.sub_lines.some((sl) => sl.description.trim()));

  const marginColor = (m: number) => m >= 30 ? "text-emerald-600" : m >= 20 ? "text-yellow-600" : "text-destructive";
  const markupCalcColor = (m: number) => m >= 30 ? "text-emerald-600" : m >= 20 ? "text-yellow-600" : "text-destructive";

  const lineIds = useMemo(() => lines.map((l) => l.id), [lines]);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
        <DialogContent className={cn(
          isFullscreen
            ? "w-screen h-screen max-w-none max-h-none rounded-none top-0 left-0 translate-x-0 translate-y-0 overflow-y-auto"
            : "max-w-[min(1200px,98vw)] max-h-[95vh] overflow-y-auto"
        )}>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-14 top-4 h-8 w-8"
            onClick={() => setIsFullscreen((v) => !v)}
            title={isFullscreen ? "Exit full screen" : "Maximize"}
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <DialogHeader>
            <DialogTitle>{isEditMode ? "Edit Estimate" : projectContext ? "New Change Order Estimate" : "New Estimate"} — Step {step} of 2</DialogTitle>
            <DialogDescription>
              {step === 1 ? "Select customer and dates" : "Add SOV line items with labor & material sub-lines"}
            </DialogDescription>
          </DialogHeader>

          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer *</Label>
                  <CustomerSelectWithCreate value={customerId} onValueChange={setCustomerId} />
                </div>
                <div>
                  <Label>Job Type *</Label>
                  <Select value={jobType} onValueChange={(v) => setJobType(v as JobType)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flooring">Flooring</SelectItem>
                      <SelectItem value="roofing">Roofing</SelectItem>
                      <SelectItem value="emergency">Emergency Response</SelectItem>
                      <SelectItem value="general">General Construction</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Project Name</Label>
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g. Building A — 3rd Floor LVP Install"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Estimate Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(txnDate, "PPP")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={txnDate} onSelect={(d) => d && setTxnDate(d)} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !expiryDate && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {expiryDate ? format(expiryDate, "PPP") : "Optional"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={expiryDate} onSelect={setExpiryDate} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div>
                <Label>Internal Notes</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes..." />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              {/* Toolbar */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                {viewMode !== "preview" && (
                <div className="flex items-center gap-2">
                  <Popover open={templatePopoverOpen} onOpenChange={setTemplatePopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 text-xs">
                        <FileText className="h-3 w-3 mr-1" /> Load Template
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2" align="start">
                      <div className="space-y-1 max-h-60 overflow-y-auto">
                        {templates?.length ? templates.map((tpl) => (
                          <button key={tpl.id} onClick={() => handleLoadTemplate(tpl)}
                            className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors">
                            <div className="font-medium">{tpl.name}</div>
                            <div className="text-xs text-muted-foreground">{(tpl.line_items as TemplateLineItem[]).length} lines · L:{tpl.labor_markup_pct}% M:{tpl.material_markup_pct}%</div>
                          </button>
                        )) : <p className="text-xs text-muted-foreground p-2">No templates yet</p>}
                      </div>
                    </PopoverContent>
                  </Popover>

                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={toggleAllExpanded}>
                    <ChevronsUpDown className="h-3 w-3 mr-1" /> {allExpanded ? "Collapse All" : "Expand All"}
                  </Button>

                  <Button variant="outline" size="sm" className="h-8 text-xs text-destructive hover:text-destructive" onClick={() => setClearConfirmOpen(true)}>
                    <Trash2 className="h-3 w-3 mr-1" /> Clear All
                  </Button>
                </div>
                )}

                <div className="inline-flex rounded-md border border-border overflow-hidden text-sm">
                  <button
                    className={cn("px-3 py-1.5 transition-colors", viewMode === "internal" ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-muted")}
                    onClick={() => setViewMode("internal")}
                  >Internal</button>
                  <button
                    className={cn("px-3 py-1.5 transition-colors border-l border-border", viewMode === "customer" ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-muted")}
                    onClick={() => setViewMode("customer")}
                  >Customer</button>
                  <button
                    className={cn("px-3 py-1.5 transition-colors border-l border-border flex items-center gap-1", viewMode === "preview" ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-muted")}
                    onClick={() => setViewMode("preview")}
                  ><Eye className="h-3 w-3" /> Preview</button>
                </div>

                {/* Print Settings */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs">
                      <FileText className="h-3 w-3 mr-1" /> Print Settings
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3" align="end">
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customer Proposal Display</p>
                      {([
                        ["show_unit_prices", "Show Unit Prices"],
                        ["show_quantities", "Show Quantities"],
                        ["show_line_items", "Show Line Items"],
                        ["show_labor_material_breakdown", "Labor/Material Breakdown"],
                        ["show_group_subtotals_only", "Group Subtotals Only"],
                        ["show_tax", "Show Tax"],
                        ["show_notes", "Show Notes"],
                        ["show_expiry", "Show Expiry Date"],
                        ["show_scope_summary", "Show Scope Summary"],
                      ] as const).map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between">
                          <Label className="text-xs">{label}</Label>
                          <Switch
                            checked={printSettings[key]}
                            onCheckedChange={(v) => setPrintSettings((prev) => ({ ...prev, [key]: v }))}
                          />
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* INTERNAL VIEW */}
              {viewMode === "internal" && (
                <TooltipProvider delayDuration={200}>
                  {/* Single horizontal scroll container — wraps sticky header + all rows so they stay aligned */}
                  <div className="overflow-x-auto">
                    <div>
                      {/* Compact toolbar (column header now lives inside each parent line) */}
                      <div className="sticky top-0 z-10 bg-background border-b border-border pb-1 pt-1 flex items-center justify-end gap-1 pr-1">
                        <button
                          type="button"
                          onClick={() => autoFitAll()}
                          className="text-muted-foreground hover:text-foreground"
                          title="Auto-fit all columns"
                        >
                          <Maximize2 className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={resetCols}
                          className="text-muted-foreground hover:text-foreground"
                          title="Reset column widths"
                        >
                          <RotateCcw className="h-3 w-3" />
                        </button>
                      </div>

                  {/* Group headers (when groups exist) */}
                  {groups.length > 0 && (
                    <div className="space-y-1 mb-3">
                      {groups.sort((a, b) => a.sort_order - b.sort_order).map((g) => (
                        <div key={g.id} className="flex items-center gap-2 p-2 rounded-md border bg-muted/30">
                          <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                          <Input
                            value={g.name}
                            onChange={(e) => updateGroup(g.id, "name", e.target.value)}
                            placeholder="Group name (e.g. Living Room, Phase 1)"
                            className="h-7 text-sm font-semibold flex-1 bg-transparent border-none shadow-none focus-visible:ring-0 p-0"
                          />
                          <Select value={g.group_type} onValueChange={(v) => updateGroup(g.id, "group_type", v)}>
                            <SelectTrigger className="h-7 w-24 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="room">Room</SelectItem>
                              <SelectItem value="phase">Phase</SelectItem>
                              <SelectItem value="trade">Trade</SelectItem>
                              <SelectItem value="custom">Custom</SelectItem>
                            </SelectContent>
                          </Select>
                          <span className="text-xs text-muted-foreground font-medium w-24 text-right">
                            {fmtCurrency(groupSubtotal(lines.filter((l) => l.group_id === g.id)))}
                          </span>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => addLineToGroup(g.id)}>
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeGroup(g.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleLineDragEnd}>
                    <SortableContext items={lineIds} strategy={verticalListSortingStrategy}>
                      {lines.map((line, idx) => {
                        const lt = calcLineTotal(line);
                        const isLS = line.unit === "LS";
                        const subLineIds = line.sub_lines.map((sl) => sl.id);
                        return (
                          <SortableLineItem key={line.id} id={line.id}>
                            <Collapsible open={line.expanded} onOpenChange={() => toggleExpand(idx)}>
                              <div className="border rounded-lg overflow-hidden">
                                <CollapsibleTrigger asChild>
                                  <div
                                    className="p-3 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                                    style={{ display: "grid", gridTemplateColumns: gridTemplate, columnGap: 6, alignItems: "start" }}
                                  >
                                    {/* 1: gutter */}
                                    <span style={{ gridColumn: "1 / 2" }} />
                                    {/* 2: Type → chevron + index */}
                                    <div className="flex items-center gap-1 overflow-hidden pt-1.5" style={{ gridColumn: "2 / 3" }}>
                                      {line.expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                                      <span className="text-xs font-mono text-muted-foreground">#{idx + 1}</span>
                                    </div>
                                    {/* 3: Label bubble (plan reference code, e.g. FT-01) — pushed left into empty product slot */}
                                    <Input
                                      value={line.label ?? ""}
                                      onChange={(e) => { e.stopPropagation(); updateLine(idx, "label", e.target.value); }}
                                      onClick={(e) => e.stopPropagation()}
                                      maxLength={20}
                                      placeholder="FT-01"
                                      title="Plan reference code (optional)"
                                      className="h-7 w-full rounded-full px-3 text-xs font-mono text-center bg-muted/50 border border-border/60 placeholder:text-muted-foreground/60 focus-visible:bg-background focus-visible:border-primary"
                                      style={{ gridColumn: "3 / 4" }}
                                    />
                                    {/* 4-7: Description (auto-grows vertically; spans through qty+unit for max width) */}
                                    <Textarea
                                      rows={1}
                                      ref={(el) => {
                                        if (el) {
                                          el.style.height = "auto";
                                          el.style.height = el.scrollHeight + "px";
                                        }
                                      }}
                                      value={line.description}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        updateLine(idx, "description", e.target.value);
                                        e.currentTarget.style.height = "auto";
                                        e.currentTarget.style.height = e.currentTarget.scrollHeight + "px";
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      placeholder="Line item description"
                                      className="min-h-8 w-full resize-none overflow-hidden py-1.5 text-sm"
                                      style={{ gridColumn: "4 / 7" }}
                                    />
                                    {/* 7: Qty (moved from track 5) */}
                                    <Input type="number" value={line.qty}
                                      onChange={(e) => { e.stopPropagation(); updateLine(idx, "qty", e.target.value); }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="h-8 w-full text-right tabular-nums"
                                      disabled={isLS}
                                      style={{ gridColumn: "7 / 8" }}
                                    />
                                    {/* 8: Unit (moved from track 6) */}
                                    <Select value={line.unit} onValueChange={(v) => {
                                      updateLine(idx, "unit", v);
                                      if (v === "LS") updateLine(idx, "qty", "1");
                                    }}>
                                      <SelectTrigger className="h-8 w-full" onClick={(e) => e.stopPropagation()} style={{ gridColumn: "8 / 9" }}><SelectValue /></SelectTrigger>
                                      <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                                    </Select>
                                    {/* 9: Sale Cost (empty for parent) */}
                                    <span style={{ gridColumn: "9 / 10" }} />
                                    {/* 10: Tax / Group selector */}
                                    {groups.length > 0 ? (
                                      <Select value={line.group_id || "__none__"} onValueChange={(v) => moveLineToGroup(line.id, v === "__none__" ? null : v)}>
                                        <SelectTrigger className="h-8 w-full text-xs" onClick={(e) => e.stopPropagation()}>
                                          <SelectValue placeholder="No group" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="__none__">No group</SelectItem>
                                          {groups.map((g) => (
                                            <SelectItem key={g.id} value={g.id}>
                                              <div className="flex items-center gap-1.5">
                                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: g.color }} />
                                                {g.name || "Unnamed"}
                                              </div>
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <span />
                                    )}
                                    {/* 11: Total */}
                                    <span className="font-semibold text-sm text-right pr-2 tabular-nums">{fmtCurrency(lt)}</span>
                                    {/* 12: 1fr spacer */}
                                    <span />
                                    {/* 13: Actions */}
                                    <div className="flex gap-0.5 justify-end" onClick={(e) => e.stopPropagation()}>
                                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => duplicateLine(idx)} title="Duplicate">
                                        <Copy className="h-3.5 w-3.5" />
                                      </Button>
                                      {lines.length > 1 && (
                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeLine(idx)}>
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </CollapsibleTrigger>

                                <CollapsibleContent>
                                  <div className="p-3 pt-1 space-y-2 border-t">
                                    {/* Per-parent column header strip — same grid as sub-lines, guarantees alignment */}
                                    <div
                                      className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider items-center"
                                      style={{ display: "grid", gridTemplateColumns: gridTemplate, columnGap: 6 }}
                                    >
                                      <span />
                                      <span className="relative text-left pl-1">Type{colHandle("type")}</span>
                                      <span className="relative text-left pl-3 truncate">Product{colHandle("product")}</span>
                                      <span className="relative text-left pl-3 pr-2 truncate">Description{colHandle("description")}</span>
                                      <span className="relative text-right pr-2">Qty{colHandle("qty")}</span>
                                      <span className="relative text-left pl-2">Unit{colHandle("unit")}</span>
                                      <span className="relative text-right pr-2 truncate">Vendor Cost{colHandle("cost")}</span>
                                      <span className="relative text-right pr-2 truncate">Markup %{colHandle("markup")}</span>
                                      <span className="relative text-right pr-2 truncate">Sale Cost{colHandle("price")}</span>
                                      <span className="relative text-left pl-1">Tax{colHandle("tax")}</span>
                                      <span className="relative text-right pr-2">Total{colHandle("total")}</span>
                                      <span />
                                      <span />
                                    </div>
                                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSubLineDragEnd(idx)}>
                                      <SortableContext items={subLineIds} strategy={verticalListSortingStrategy}>
                                        {line.sub_lines.map((sl, si) => {
                                          const cost = parseFloat(sl.cost_price) || 0;
                                          const price = parseFloat(sl.unit_price) || 0;
                                          const markup = parseFloat(sl.markup_pct) || 0;
                                          const slIsLS = sl.unit === "LS";
                                          const triggerUnit = getLineTriggerUnitDraft(line);
                                          const isAutoMatched = sl.quantity_mode === "auto" && triggerUnit && sl.unit === triggerUnit;
                                          return (
                                            <SortableSubLineItem key={sl.id} id={sl.id}>
                                              <div className="space-y-0.5">
                                                <div
                                                  className="items-start"
                                                  style={{ display: "grid", gridTemplateColumns: gridTemplate, columnGap: 6 }}
                                                 >
                                                   <span />
                                                   <div className="flex items-center justify-start overflow-hidden">
                                                     <Badge variant="outline" className={`text-xs ${sl.type === "labor" ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800" : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800"}`}>
                                                      {sl.type === "labor" ? "Labor" : "Matl"}
                                                    </Badge>
                                                  </div>
                                                  {/* Product picker */}
                                                  <Select
                                                    value={sl.product_service_id || "custom"}
                                                    onValueChange={(v) => { if (v !== "custom") selectProduct(idx, si, v); }}
                                                  >
                                                    <SelectTrigger className="h-8 text-xs w-full min-w-0 [&>span]:truncate [&>span]:block [&>span]:text-left" title={allProducts?.find(p => p.id === sl.product_service_id)?.name || ""}><SelectValue placeholder="Product..." /></SelectTrigger>
                                                    <SelectContent>
                                                      <SelectItem value="custom">Custom</SelectItem>
                                                      {(() => {
                                                        const filtered = allProducts?.filter((p) => {
                                                          if (sl.type === "labor") return p.category === "labor";
                                                          return p.category === "material" || p.category === "equipment" || p.category === "other";
                                                        }) ?? [];
                                                        const priorityCodes = JOB_TYPE_PRIORITY[jobType] ?? [];
                                                        const prioritySet = new Set(priorityCodes);
                                                        const prioritized = filtered.filter(p => prioritySet.has(p.code));
                                                        const others = filtered.filter(p => !prioritySet.has(p.code));
                                                        const jobLabel = jobType === 'flooring' ? 'Flooring' : jobType === 'roofing' ? 'Roofing' : jobType === 'emergency' ? 'Emergency' : '';
                                                        return (
                                                          <>
                                                            {prioritized.length > 0 && jobLabel && (
                                                              <SelectItem value="__header_priority__" disabled className="text-[10px] font-bold text-muted-foreground">
                                                                ── {jobLabel} ──
                                                              </SelectItem>
                                                            )}
                                                            {prioritized.map((p) => (
                                                              <SelectItem key={p.id} value={p.id}>
                                                                <span className="font-mono text-[10px] mr-1">{p.code}</span> {p.name}
                                                              </SelectItem>
                                                            ))}
                                                            {others.length > 0 && prioritized.length > 0 && (
                                                              <SelectItem value="__header_other__" disabled className="text-[10px] font-bold text-muted-foreground">
                                                                ── Other ──
                                                              </SelectItem>
                                                            )}
                                                            {others.map((p) => (
                                                              <SelectItem key={p.id} value={p.id}>
                                                                <span className="font-mono text-[10px] mr-1">{p.code}</span> {p.name}
                                                              </SelectItem>
                                                            ))}
                                                          </>
                                                        );
                                                      })()}
                                                    </SelectContent>
                                                  </Select>
                                                  <Textarea
                                                    value={sl.description}
                                                    onChange={(e) => {
                                                      updateSubLine(idx, si, "description", e.target.value);
                                                      const ta = e.currentTarget;
                                                      ta.style.height = "auto";
                                                      ta.style.height = ta.scrollHeight + "px";
                                                    }}
                                                    ref={(el) => {
                                                      if (el) {
                                                        el.style.height = "auto";
                                                        el.style.height = el.scrollHeight + "px";
                                                      }
                                                    }}
                                                    placeholder={sl.type === "labor" ? "Labor desc" : "Material desc"}
                                                    rows={1}
                                                    className="min-h-8 w-full text-sm py-1.5 resize-none overflow-hidden leading-tight"
                                                  />
                                                  {/* Qty with auto/manual indicator */}
                                                  <div className="relative flex items-center w-full">
                                                    <Input type="number" value={sl.qty} onChange={(e) => updateSubLine(idx, si, "qty", e.target.value)}
                                                      className={cn("h-8 w-full pr-6", isAutoMatched && "border-blue-300 dark:border-blue-700")} disabled={slIsLS || sl.quantity_mode === "auto" || sl.quantity_mode === "formula"} />
                                                    {sl.quantity_mode === "formula" ? (
                                                      <Popover>
                                                        <PopoverTrigger asChild>
                                                          <button type="button" className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted">
                                                            <Calculator className="h-3 w-3 text-emerald-500" />
                                                          </button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-64 p-3" side="top">
                                                          <div className="space-y-2">
                                                            <div className="flex items-center justify-between">
                                                              <p className="text-xs font-semibold">Formula Editor</p>
                                                              <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1" onClick={() => toggleQuantityMode(idx, si)}>
                                                                Switch to Auto
                                                              </Button>
                                                            </div>
                                                            <Select
                                                              value={sl.quantity_formula?.kind ?? "coverage"}
                                                              onValueChange={(v) => {
                                                                const f = sl.quantity_formula ?? { kind: "coverage" as const, source: "parent_qty" as const, source_unit: line.unit };
                                                                updateFormula(idx, si, { ...f, kind: v as "coverage" | "multiplier" });
                                                              }}
                                                            >
                                                              <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                                                              <SelectContent>
                                                                <SelectItem value="coverage">Coverage (Qty / Value)</SelectItem>
                                                                <SelectItem value="multiplier">Multiplier (Qty x Value)</SelectItem>
                                                              </SelectContent>
                                                            </Select>
                                                            {(sl.quantity_formula?.kind ?? "coverage") === "coverage" ? (
                                                              <div className="flex items-center gap-1">
                                                                <Label className="text-[10px] w-24">Coverage per unit</Label>
                                                                <Input type="number" className="h-7 text-xs" step="0.01"
                                                                  value={sl.quantity_formula?.coverage_per_unit ?? ""}
                                                                  onChange={(e) => {
                                                                    const f = sl.quantity_formula ?? { kind: "coverage" as const, source: "parent_qty" as const, source_unit: line.unit };
                                                                    updateFormula(idx, si, { ...f, kind: "coverage", coverage_per_unit: parseFloat(e.target.value) || 0 });
                                                                  }}
                                                                />
                                                              </div>
                                                            ) : (
                                                              <div className="flex items-center gap-1">
                                                                <Label className="text-[10px] w-24">Multiplier</Label>
                                                                <Input type="number" className="h-7 text-xs" step="0.01"
                                                                  value={sl.quantity_formula?.multiplier ?? ""}
                                                                  onChange={(e) => {
                                                                    const f = sl.quantity_formula ?? { kind: "multiplier" as const, source: "parent_qty" as const, source_unit: line.unit };
                                                                    updateFormula(idx, si, { ...f, kind: "multiplier", multiplier: parseFloat(e.target.value) || 0 });
                                                                  }}
                                                                />
                                                              </div>
                                                            )}
                                                            <Select
                                                              value={sl.quantity_formula?.round_mode ?? "none"}
                                                              onValueChange={(v) => {
                                                                const f = sl.quantity_formula ?? { kind: "coverage" as const, source: "parent_qty" as const, source_unit: line.unit };
                                                                updateFormula(idx, si, { ...f, round_mode: v as any });
                                                              }}
                                                            >
                                                              <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Rounding" /></SelectTrigger>
                                                              <SelectContent>
                                                                <SelectItem value="none">No rounding</SelectItem>
                                                                <SelectItem value="up">Round up</SelectItem>
                                                                <SelectItem value="down">Round down</SelectItem>
                                                                <SelectItem value="nearest">Round nearest</SelectItem>
                                                              </SelectContent>
                                                            </Select>
                                                            <div className="flex items-center gap-1">
                                                              <Label className="text-[10px] w-24">Min qty</Label>
                                                              <Input type="number" className="h-7 text-xs"
                                                                value={sl.quantity_formula?.min_qty ?? ""}
                                                                onChange={(e) => {
                                                                  const f = sl.quantity_formula ?? { kind: "coverage" as const, source: "parent_qty" as const, source_unit: line.unit };
                                                                  updateFormula(idx, si, { ...f, min_qty: parseFloat(e.target.value) || undefined });
                                                                }}
                                                              />
                                                            </div>
                                                            <div className="pt-1 border-t text-[10px] text-muted-foreground">
                                                              Result: <span className="font-mono font-semibold text-foreground">{sl.qty}</span> {sl.unit}
                                                              {" "}(from {line.qty} {line.unit} parent)
                                                            </div>
                                                          </div>
                                                        </PopoverContent>
                                                      </Popover>
                                                    ) : (
                                                      <Tooltip>
                                                        <TooltipTrigger asChild>
                                                          <button
                                                            type="button"
                                                            className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted"
                                                            onClick={() => toggleQuantityMode(idx, si)}
                                                          >
                                                            {sl.quantity_mode === "manual" ? (
                                                              <Pencil className="h-3 w-3 text-amber-500" />
                                                            ) : (
                                                              <Link2 className="h-3 w-3 text-blue-500" />
                                                            )}
                                                          </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top">
                                                          {sl.quantity_mode === "manual"
                                                            ? "Manual — click for formula mode"
                                                            : "Auto: matches parent qty — click for manual"}
                                                        </TooltipContent>
                                                      </Tooltip>
                                                    )}
                                                  </div>
                                                  <Select value={sl.unit} onValueChange={(v) => updateSubLine(idx, si, "unit", v)}>
                                                    <SelectTrigger className="h-8 w-full"><SelectValue /></SelectTrigger>
                                                    <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                                                  </Select>
                                                  {(() => {
                                                     const slProduct = sl.product_service_id ? allProducts?.find(p => p.id === sl.product_service_id) : null;
                                                     const hasCostMapping = slProduct?.qty_mapping?.use_as === 'cost_price';
                                                     return (
                                                       <div className="relative w-full">
                                                         <CurrencyCell
                                                           value={sl.cost_price}
                                                           onChange={(v) => updateSubLine(idx, si, "cost_price", v)}
                                                           title={hasCostMapping ? `Auto: ${slProduct?.qty_mapping?.formula}` : undefined}
                                                         />
                                                         {hasCostMapping && <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px]" title={slProduct?.qty_mapping?.note}>🔗</span>}
                                                       </div>
                                                     );
                                                   })()}
                                                  <div className="w-full">
                                                    <Input type="number" value={sl.markup_pct} onChange={(e) => updateSubLine(idx, si, "markup_pct", e.target.value)} className="h-8 w-full text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" style={{ fontVariantNumeric: "tabular-nums" }} />
                                                  </div>
                                                  <div className="w-full" title={slIsLS ? "Total" : "Unit price"}>
                                                     <CurrencyCell value={sl.unit_price} onChange={(v) => updateSubLine(idx, si, "unit_price", v)} />
                                                   </div>
                                                  {/* Tax cell — merges Tax toggle + Vendor Tax checkbox */}
                                                  <div className="flex items-center gap-1.5 min-w-0 pr-3">
                                                    {sl.type === "material" ? (
                                                      <>
                                                        <div className="flex items-center gap-1" title="Taxable (QB)">
                                                          <Switch checked={sl.taxable} onCheckedChange={(v) => updateSubLine(idx, si, "taxable", v)} className="scale-75" />
                                                          <span className="text-[10px] text-muted-foreground">Tax</span>
                                                        </div>
                                                        {sl.taxable && (
                                                          <div className="flex items-center gap-1" title="Vendor paid sales tax on this material">
                                                            <Checkbox checked={sl.apply_vendor_tax} onCheckedChange={(v) => updateSubLine(idx, si, "apply_vendor_tax", !!v)} />
                                                            <span className="text-[10px] text-muted-foreground">Vendor</span>
                                                          </div>
                                                        )}
                                                      </>
                                                    ) : (
                                                      <span className="text-[10px] text-muted-foreground">—</span>
                                                    )}
                                                  </div>
                                                  <div className="min-w-0 text-right pl-1 pr-1" style={{ fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    <span className="text-sm font-medium">{fmtCurrency(subLineBilled(sl))}</span>
                                                  </div>
                                                  <span />
                                                  <Button variant="ghost" size="icon" className="h-7 w-7 justify-self-end" onClick={() => removeSubLine(idx, si)}>
                                                    <Trash2 className="h-3 w-3" />
                                                  </Button>
                                                </div>
                                                {cost > 0 && (
                                                  <p className={cn("text-[11px] pl-4 ml-[52px]", markupCalcColor(markup))}>
                                                    {sl.taxable && sl.apply_vendor_tax && sl.vendor_tax_rate > 0
                                                      ? (() => {
                                                          const vt = sl.vendor_tax_rate;
                                                          const effectiveCost = cost * (1 + vt / 100);
                                                          const preTax = effectiveCost * (1 + markup / 100);
                                                          return `Cost ${fmtCurrency(cost)} + vendor tax ${vt}% = ${fmtCurrency(effectiveCost)} → ×${(1 + markup / 100).toFixed(2)} = ${fmtCurrency(preTax)} → + tax ${vt}% = ${fmtCurrency(price)}/${sl.unit}`;
                                                        })()
                                                      : `Cost ${fmtCurrency(cost)} + ${markup.toFixed(0)}% = ${fmtCurrency(price)}`}
                                                  </p>
                                                )}
                                              </div>
                                            </SortableSubLineItem>
                                          );
                                        })}
                                      </SortableContext>
                                    </DndContext>
                                    <div className="flex gap-2 pl-4 pt-1">
                                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addSubLine(idx, "labor")}>
                                        <Plus className="h-3 w-3 mr-1" /> Labor
                                      </Button>
                                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addSubLine(idx, "material")}>
                                        <Plus className="h-3 w-3 mr-1" /> Material
                                      </Button>
                                    </div>
                                  </div>
                                </CollapsibleContent>
                              </div>
                            </Collapsible>
                          </SortableLineItem>
                        );
                      })}
                    </SortableContext>
                  </DndContext>

                    </div>
                  </div>

                  {/* Tax note */}
                  <p className="text-xs text-muted-foreground italic">
                    Tax rate applied at QB sync per company tax settings.
                  </p>
                </TooltipProvider>
              )}

              {/* CUSTOMER VIEW */}
              {viewMode === "customer" && (
                <div className="space-y-2">
                  {lines.map((line, idx) => {
                    const qty = parseFloat(line.qty) || 0;
                    const lineTotal = line.sub_lines.reduce((s, sl) => s + subLineBilled(sl), 0);
                    const combinedUnitPrice = qty > 0 ? lineTotal / qty : 0;
                    return (
                      <div key={line.id} className="flex items-center gap-2 p-3 border rounded-lg">
                        <span className="text-xs font-mono text-muted-foreground w-6">#{idx + 1}</span>
                        <div className="flex-1 flex items-center gap-1">
                          <Input
                            value={line.customer_description || line.description}
                            onChange={(e) => updateLine(idx, "customer_description", e.target.value)}
                            placeholder="Customer-facing description"
                            className="h-8 flex-1"
                          />
                          <Pencil className="h-3 w-3 text-muted-foreground shrink-0" />
                        </div>
                        <span className="text-sm text-muted-foreground w-16 text-right">{qty} {line.unit}</span>
                        <span className="text-sm text-muted-foreground w-24 text-right">@ {fmtCurrency(combinedUnitPrice)}</span>
                        <span className="font-semibold text-sm w-24 text-right">{fmtCurrency(lineTotal)}</span>
                        {lines.length > 1 && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeLine(idx)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* PREVIEW MODE — Customer-facing document preview */}
              {viewMode === "preview" && (
                <div className="space-y-3">
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPrintPreview(true)}
                    >
                      <Printer className="h-4 w-4 mr-1" /> Print / Save PDF
                    </Button>
                  </div>
                  <div id="estimate-preview-container" className="border rounded-lg bg-white dark:bg-background shadow-sm overflow-hidden">
                    <EstimatePrintTemplate data={buildPrintData} />
                  </div>
                </div>
              )}

              {viewMode !== "preview" && (
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => setLines((p) => [...p, emptyLine(p.length)])}>
                    <Plus className="h-4 w-4 mr-1" /> Add Line Item
                  </Button>
                  <Button variant="outline" size="sm" onClick={addGroup}>
                    <Plus className="h-4 w-4 mr-1" /> Add Group
                  </Button>
                </div>
              )}

              {/* Master Markup + Profit Summary — hidden in preview */}
              {viewMode !== "preview" && (
              <div className={cn("grid gap-4", viewMode === "internal" ? "grid-cols-2" : "grid-cols-1")}>
                {viewMode === "internal" && (
                  <div className="flex flex-col gap-2 p-3 border rounded-lg bg-muted/20">
                    <Label className="text-xs font-semibold">Markup Controls</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-14">Labor</span>
                      <Input type="number" value={laborMarkup} onChange={(e) => setLaborMarkup(e.target.value)} className="h-8 w-20 min-w-[80px]" />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-14">Material</span>
                      <Input type="number" value={materialMarkup} onChange={(e) => setMaterialMarkup(e.target.value)} className="h-8 w-20 min-w-[80px]" />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                    <Button size="sm" variant="outline" className="h-8 text-xs self-start" onClick={applyMasterMarkup}>Apply to All</Button>
                  </div>
                )}

                {viewMode === "internal" && (
                  <div className="border rounded-lg p-3 space-y-1 bg-yellow-50/50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800">
                    <div className="flex items-center gap-1 text-xs font-semibold text-yellow-700 dark:text-yellow-400 mb-1">
                      <AlertTriangle className="h-3 w-3" /> INTERNAL — Not shown to customer
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-muted-foreground text-xs">
                          <th className="text-left font-normal pb-1"></th>
                          <th className="text-right font-normal pb-1">Cost</th>
                          <th className="text-right font-normal pb-1">Billed</th>
                          <th className="text-right font-normal pb-1">Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="text-muted-foreground">Labor</td>
                          <td className="text-right">{fmtCurrency(totals.laborCost)}</td>
                          <td className="text-right">{fmtCurrency(totals.laborBilled)}</td>
                          <td className={cn("text-right", marginColor(totals.laborMargin))}>{totals.laborMargin.toFixed(1)}%</td>
                        </tr>
                        <tr>
                          <td className="text-muted-foreground">Material (raw)</td>
                          <td className="text-right">{fmtCurrency(totals.materialCost)}</td>
                          <td className="text-right">{fmtCurrency(totals.materialBilled)}</td>
                          <td className="text-right text-muted-foreground">—</td>
                        </tr>
                        {totals.vendorTaxOnMaterials > 0 && (
                          <>
                            <tr>
                              <td className="text-muted-foreground text-xs pl-2">Vendor Purch. Tax</td>
                              <td className="text-right text-xs">{fmtCurrency(totals.vendorTaxOnMaterials)}</td>
                              <td className="text-right text-xs text-muted-foreground">—</td>
                              <td className="text-right text-xs text-muted-foreground">—</td>
                            </tr>
                            <tr>
                              <td className="text-muted-foreground text-xs pl-2">Landed Material</td>
                              <td className="text-right text-xs">{fmtCurrency(totals.landedMaterialCost)}</td>
                              <td className="text-right">{fmtCurrency(totals.materialBilled)}</td>
                              <td className={cn("text-right", marginColor(totals.materialMargin))}>{totals.materialMargin.toFixed(1)}%</td>
                            </tr>
                          </>
                        )}
                        {totals.vendorTaxOnMaterials <= 0 && (
                          <tr>
                            <td></td><td></td><td></td>
                            <td className={cn("text-right", marginColor(totals.materialMargin))}>{totals.materialMargin.toFixed(1)}%</td>
                          </tr>
                        )}
                        {totals.embeddedCustomerTax > 0 && (
                          <tr>
                            <td className="text-muted-foreground text-xs pl-2">Embedded Cust. Tax</td>
                            <td className="text-right text-xs text-muted-foreground">—</td>
                            <td className="text-right text-xs">{fmtCurrency(totals.embeddedCustomerTax)}</td>
                            <td className="text-right text-xs text-muted-foreground">—</td>
                          </tr>
                        )}
                        <tr className="font-semibold border-t">
                          <td>Total</td>
                          <td className="text-right">{fmtCurrency(totals.totalCost)}</td>
                          <td className="text-right">{fmtCurrency(totals.totalBilled)}</td>
                          <td className={cn("text-right", marginColor(totals.margin))}>{totals.margin.toFixed(1)}%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/* Tax State + Rate Controls */}
              {viewMode !== "preview" && (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs text-muted-foreground whitespace-nowrap">Tax State</Label>
                    <Select value={taxState} onValueChange={(v) => { setTaxState(v); lookupTaxForState(v); }}>
                      <SelectTrigger className="h-7 w-36 text-xs">
                        <SelectValue placeholder="Select state…" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {US_STATES.map((s) => (
                          <SelectItem key={s.code} value={s.code}>{s.code} — {s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs text-muted-foreground whitespace-nowrap">Tax Rate</Label>
                    <Input
                      type="number"
                      className="h-7 w-20 text-xs"
                      value={taxRate}
                      onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                      step="0.01"
                      min="0"
                      max="100"
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                  </div>
                </div>
              )}

              {/* Footer Subtotals */}
              <div className="border rounded-lg p-4 space-y-1 bg-muted/30">
                <div className="flex justify-between text-sm">
                  <span>{viewMode === "customer" ? "Labor Subtotal" : "Labor Subtotal (billed)"}</span><span>{fmtCurrency(totals.laborBilled)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>{viewMode === "customer" ? "Material Subtotal" : "Material Subtotal (billed)"}</span><span>{fmtCurrency(totals.materialBilled)}</span>
                </div>
                {viewMode === "internal" && totals.vendorTaxOnMaterials > 0 && (
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Vendor Tax Paid (materials)</span><span>{fmtCurrency(totals.vendorTaxOnMaterials)}</span>
                  </div>
                )}
                {(totals.embeddedCustomerTax > 0 || totals.vendorTaxOnMaterials > 0) && (
                  <div className="flex justify-between text-sm text-muted-foreground italic">
                    <span>Includes Sales Tax ({totals.taxRate.toFixed(2)}%)</span><span>{fmtCurrency(totals.embeddedCustomerTax + totals.vendorTaxOnMaterials)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-base border-t pt-1 mt-1">
                  <span>Total Amount</span><span>{fmtCurrency(totals.totalBilled)}</span>
                </div>
              </div>

              {viewMode !== "preview" && (
                <div>
                  <Label>Customer Notes (visible to customer — maps to QB CustomerMemo)</Label>
                  <Textarea
                    value={customerNotes}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                    placeholder="Notes visible to customer on the estimate..."
                    className="min-h-[80px]"
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between pt-2">
            <div>
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)}>
                  <ArrowLeft className="mr-1 h-4 w-4" /> Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {step === 1 ? (
                <Button onClick={() => setStep(2)} disabled={!canNext1}>
                  Next <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => handleSave("not_synced")} disabled={!hasLines || isSaving}>
                    {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    {isEditMode ? "Save" : "Save as Draft"}
                  </Button>
                  {!projectContext && (
                    <Button onClick={() => handleSave("pending")} disabled={!hasLines || isSaving}>
                      {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                      {isEditMode ? "Update & Sync to QB" : "Save & Send to QB"}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template load mode dialog */}
      <AlertDialog open={!!pendingTemplate} onOpenChange={(v) => { if (!v) setPendingTemplate(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Load Template: {pendingTemplate?.name}</AlertDialogTitle>
            <AlertDialogDescription>
              You already have line items. How would you like to load the template?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-2 sm:justify-end">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => applyTemplate("append")} className="bg-secondary text-secondary-foreground hover:bg-secondary/80">
              Append to existing lines
            </AlertDialogAction>
            <AlertDialogAction onClick={() => applyTemplate("replace")}>
              Replace all lines
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear all confirmation */}
      <AlertDialog open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all line items?</AlertDialogTitle>
            <AlertDialogDescription>This will remove all line items and sub-lines. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setLines([emptyLine()]); setClearConfirmOpen(false); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Clear All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Print Preview Dialog */}
      <Dialog open={showPrintPreview} onOpenChange={setShowPrintPreview}>
        <DialogContent className={cn(
          isFullscreen
            ? "w-screen h-screen max-w-none max-h-none rounded-none top-0 left-0 translate-x-0 translate-y-0 overflow-y-auto print:shadow-none"
            : "max-w-4xl max-h-[90vh] overflow-y-auto print:shadow-none"
        )} hideClose>
          <DialogHeader className="flex flex-row items-center justify-between print:hidden">
            <DialogTitle>Print Preview</DialogTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPrintPreview(false)}
              >
                Close
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  exportPdf(<EstimatePrintTemplate data={buildPrintData} />, `Estimate - ${customerName || "Draft"}`);
                }}
              >
                <Printer className="h-4 w-4 mr-1" /> Print
              </Button>
            </div>
          </DialogHeader>
          <DialogDescription className="sr-only">Printable estimate preview</DialogDescription>
          <div id="print-preview-content" className="bg-white dark:bg-background rounded-lg">
            <EstimatePrintTemplate data={buildPrintData} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
